import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Gift, Copy, Users, Coins, Check, ExternalLink } from 'lucide-react';

const ReferralSection = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await api.get('/referral/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (stats?.referral_link) {
      try {
        await navigator.clipboard.writeText(stats.referral_link);
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast.error('Failed to copy link');
      }
    }
  };

  if (loading) {
    return (
      <Card className="border-primary/20">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-1/3"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Gift className="w-4 h-4 text-white" />
            </div>
            Invite & Earn Credits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Value proposition */}
          <p className="text-sm text-muted-foreground">
            Invite friends. Get <span className="font-semibold text-primary">100 credits</span> when they create their first image.
            They get <span className="font-semibold text-primary">50 bonus credits</span> too!
          </p>

          {/* Referral link */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Your Referral Link</label>
            <div className="flex gap-2">
              <div className="flex-1 px-3 py-2 bg-background/60 rounded-lg border border-border text-sm font-mono truncate">
                {stats.referral_link}
              </div>
              <Button 
                size="sm" 
                onClick={copyReferralLink}
                className="shrink-0"
                data-testid="copy-referral-link"
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            <div className="text-center p-3 bg-background/40 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-xl font-bold text-primary">
                <Users className="w-4 h-4" />
                {stats.total_referrals}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Invited</p>
            </div>
            <div className="text-center p-3 bg-background/40 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-xl font-bold text-green-500">
                <Check className="w-4 h-4" />
                {stats.successful_referrals}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Converted</p>
            </div>
            <div className="text-center p-3 bg-background/40 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-xl font-bold text-amber-500">
                <Coins className="w-4 h-4" />
                {stats.credits_earned}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Earned</p>
            </div>
          </div>

          {/* Pending referrals note */}
          {stats.pending_referrals > 0 && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              {stats.pending_referrals} friend{stats.pending_referrals > 1 ? 's' : ''} signed up - waiting for first creation
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ReferralSection;
